import { useQueryClient } from '@tanstack/react-query'
import { createContext, useContext, useState, useEffect } from 'react'
import { authAPI } from '../lib/api'
import toast from 'react-hot-toast'

const AuthContext = createContext()

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function AuthProvider({ children }) {
  const queryClient = useQueryClient()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    checkAuth()
    const timer = setInterval(checkAuth, 30000)
    window.addEventListener('focus', checkAuth)
    return () => { clearInterval(timer); window.removeEventListener('focus', checkAuth) }
  }, [])

  const checkAuth = async () => {
    const token = localStorage.getItem('token')
    try {
      if (token) {
        const response = await authAPI.getProfile()
        if (localStorage.getItem('token') !== token) return
        queryClient.clear()
        setUser(response.data.user)
        localStorage.setItem('user', JSON.stringify(response.data.user))
      }
    } catch (error) {
      if (localStorage.getItem('token') !== token) return
      queryClient.clear()
      localStorage.removeItem('selectedShop')
      localStorage.removeItem('selectedShopId')
      localStorage.removeItem('last_bottling_shop_id')
      setUser(null)
      localStorage.removeItem('token')
      localStorage.removeItem('user')
    } finally {
      setLoading(false)
    }
  }

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials)
      const { token, user } = response.data
      
      localStorage.setItem('token', token)
      localStorage.setItem('user', JSON.stringify(user))
      queryClient.clear()
      localStorage.removeItem('selectedShop')
      localStorage.removeItem('selectedShopId')
      localStorage.removeItem('last_bottling_shop_id')
      setUser(user)
      
      toast.success('Login successful!')
      return { success: true }
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Login failed' }
    }
  }

  const setUserFromStorage = () => { checkAuth() }

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData)
      const { token, user } = response.data
      
      localStorage.setItem('token', token)
      localStorage.setItem('user', JSON.stringify(user))
      queryClient.clear()
      localStorage.removeItem('selectedShop')
      localStorage.removeItem('selectedShopId')
      localStorage.removeItem('last_bottling_shop_id')
      setUser(user)
      
      toast.success('Registration successful!')
      return { success: true }
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Registration failed' }
    }
  }

  const logout = async () => {
    try {
      // Call server logout endpoint
      await authAPI.logout();
      
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      queryClient.clear()
      localStorage.removeItem('selectedShop')
      localStorage.removeItem('selectedShopId')
      localStorage.removeItem('last_bottling_shop_id')
      setUser(null)
      toast.success('Logged out successfully')
    } catch (error) {
      console.error('Logout error:', error)
      // Even if server logout fails, clear local storage
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      queryClient.clear()
      localStorage.removeItem('selectedShop')
      localStorage.removeItem('selectedShopId')
      localStorage.removeItem('last_bottling_shop_id')
      setUser(null)
      toast.success('Logged out successfully')
    }
  }

  const updateProfile = async (data) => {
    try {
      const response = await authAPI.updateProfile(data)
      const updatedUser = { ...user, firstName: data.firstName ?? user.firstName, lastName: data.lastName ?? user.lastName, phone: data.phone ?? user.phone }
      setUser(updatedUser)
      localStorage.setItem('user', JSON.stringify(updatedUser))
      toast.success('Profile updated successfully!')
      return { success: true }
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Update failed' }
    }
  }

  const changePassword = async (data) => {
    try {
      await authAPI.changePassword(data)
      toast.success('Password changed successfully!')
      return { success: true }
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Password change failed' }
    }
  }

  const value = {
    user,
    isLoading: loading,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    setUserFromStorage,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
} 